import React, { useEffect, useState, useContext } from 'react'
import { Separator, SocialBar } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { useLocation } from '@fs/zion-router'
import Head, { Title } from '@fs/zion-head'
import ContentCardRow from '../ContentCardRow'
import TopDetailBar from '../TopDetailBar'
import { PlaylistContext } from '../PlaylistContext'
import DownloadableResources from '../DownloadableResources'
import { markGroupsofItemsInPlaylist } from '../../global/playlistHelper'
import useIsMobile from '../../hooks/useIsMobile'
import { useCurrentSession } from '../SessionProvider'
import SpotlightGradient from '../SpotlightGradient'
import SessionView from '../session-view/SessionView'
import { getLanguageFromLocale } from '../../helpers/languageNameInLanguage'
import getCreatorsNames from '../../helpers/creatorsNames'
import BS_OBJECT_TYPES from '../../api/brightspot/objectTypeIds'
import placeholderImg from '../../images/stageWithLogo.jpg'

const cardsContainerStyle = css`
  word-break: break-word;
`
const barOptionsCss = css`
  color: white;
  display: flex;
  align-items: center;

  & button[iconsize] {
    margin-bottom: 0 !important;
  }
`
const shareTextCss = css`
  margin-right: 16px;
  font-size: 14px;
`
const SessionDetail = () => {
  const location = useLocation()
  const { data, loading, model } = useCurrentSession()
  const [t] = useTranslation()
  const [sessionsData, setSessionsData] = useState()
  const [subheading, setSubheading] = useState()
  const [videoProviderList, setVideoProviderList] = useState()
  const [profileImage, setProfileImage] = useState()
  const [isInPlaylist, setIsInPlaylist] = useState(false)
  const { playlist } = useContext(PlaylistContext)
  const isMobile = useIsMobile()
  const [baseData, setBaseData] = useState()

  usePageView({ page_detail: `${location.pathname}` })

  useEffect(() => {
    if (data && model) {
      setBaseData(data[model])
    }
  }, [data, model])

  useEffect(() => {
    if (baseData?.videos) {
      setVideoProviderList(baseData?.videos)
    }
  }, [baseData])

  useEffect(() => {
    if (baseData?.suggestedContent) {
      // TO DO: get brightspot to stop sending in tags as suggested content
      const suggestContentWithTagsFilteredOut = baseData?.suggestedContent?.filter((item) => {
        return item.objectTypeId !== BS_OBJECT_TYPES.TAG
      })

      const suggestedContent = [
        {
          title: t('sessions.suggested-content', 'Suggested Content'),
          // Limit number of suggested content items to 8
          items: suggestContentWithTagsFilteredOut.slice(0, 8),
          keywords: baseData.keywords,
        },
      ]

      const playlistMarkedList = markGroupsofItemsInPlaylist({ playlist, items: suggestedContent })
      setSessionsData(playlistMarkedList)
    }
  }, [playlist, t, baseData])

  useEffect(() => {
    if (baseData?.creators) {
      const names = getCreatorsNames(baseData?.creators)
      setSubheading(names)

      const photo =
        baseData?.creators?.length === 1 ? baseData.creators[0].profilePhoto?.custom?.image?.src : placeholderImg
      setProfileImage(photo)
    }
  }, [baseData])

  useEffect(() => {
    if (playlist) {
      const match = playlist.find((item) => {
        return item.cmsId === baseData?.contentId
      })
      if (match) {
        setIsInPlaylist(true)
      } else {
        setIsInPlaylist(false)
      }
    }
  }, [baseData, playlist])

  const downloadableResources = [...(baseData?.classSyllabus || []), ...(baseData?.slideDeck || [])]

  return (
    <>
      <Title breadcrumbs={[t('site-title', 'RootsTech 2021')]}>{baseData?.title}</Title>
      <Head>
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:title"
          content={`${baseData?.title} \u2022 ${t('site-title', 'RootsTech 2021')} \u2022 FamilySearch `}
        />
        <meta property="og:description" content={baseData?.description} />
        <meta property="og:image" content={profileImage} />
      </Head>

      <SpotlightGradient flavor="blueberry" size={isMobile ? 'sm' : 'lg'} />

      <SessionView
        nav={
          <TopDetailBar backText={t('common-ui:back.action', 'Back')} backLink="/search">
            <div css={barOptionsCss}>
              {!isMobile && (
                <div css={shareTextCss}>{t('sessions.share-with-others.label', 'Share this session with others')}</div>
              )}
              <SocialBar
                useIconButtons
                apps={['facebook', 'twitter', 'whatsApp']}
                sharable={{
                  url: encodeURIComponent(window.location.href),
                  subject: t('session.sharing-subject.label', 'Rootstech Session: {title}', {
                    title: baseData?.title,
                  }),
                }}
              />
            </div>
          </TopDetailBar>
        }
        heading={baseData?.title}
        subheading={subheading}
        creators={baseData?.creators}
        description={baseData?.description}
        profilePhoto={profileImage}
        data={baseData}
        loading={loading}
        videoProviderList={videoProviderList}
        isInPlaylist={isInPlaylist}
        spokenLanguage={getLanguageFromLocale({ locale: baseData?.locale, parentLocale: baseData?.parentLocale })}
        isMainStageLiveVariant={baseData?.variant === 'Mainstage Session'}
      >
        <Separator size="xxl" />

        <div>
          {baseData?.additionalExperiences?.[0] && (
            <>
              <ContentCardRow
                items={baseData?.additionalExperiences}
                headingText={t('series.additional-experiences', 'Additional Experiences')}
                openInNewTab
              />
              <Separator size="xl" />
            </>
          )}

          {downloadableResources.length > 0 && (
            <>
              <DownloadableResources downloadableResources={downloadableResources} />
              <Separator size="xl" />
            </>
          )}

          {sessionsData?.[0] && (
            <>
              <div css={cardsContainerStyle}>
                <ContentCardRow
                  headingText={sessionsData?.[0]?.title}
                  useHeadingDivider
                  items={sessionsData?.[0].items}
                  tags={sessionsData?.[0].keywords}
                />
              </div>
            </>
          )}
        </div>
      </SessionView>
    </>
  )
}

export default SessionDetail
